// Sign-in return-target helpers.
//
// The backend's `createBrowserAuthMiddleware` redirects anonymous
// browsers to `/sign-in?redirect_url=<encoded original path>`. The SPA
// must honor that target after a successful sign-in instead of always
// landing on `/`.
//
// Same-origin safety: only accept paths that start with a single
// forward slash. Reject absolute URLs, protocol-relative URLs
// (`//evil.com`), backslash-laundered paths, and control characters.
//
// DOM access is funneled through `globalThis` so this module
// type-checks under both the client tsconfig (DOM lib) and the
// server tsconfig (no DOM lib) — that lets the pure helpers live in
// `test/redirect.test.ts` without splitting the file.

const CONTROL_CHARS = /[\x00-\x1f\x7f]/;

interface LocationLike {
  pathname: string;
  search: string;
  hash: string;
}

function readLocation(): LocationLike | null {
  const g = globalThis as { location?: LocationLike };
  return g.location ?? null;
}

export function safeRedirectTarget(
  input: string | null | undefined,
  fallback = "/",
): string {
  if (typeof input !== "string" || input.length === 0) return fallback;
  if (!input.startsWith("/")) return fallback;
  if (input.startsWith("//")) return fallback;
  if (input.includes("\\")) return fallback;
  if (CONTROL_CHARS.test(input)) return fallback;
  return input;
}

export function getRedirectFromLocation(fallback = "/"): string {
  const loc = readLocation();
  if (!loc) return fallback;
  const params = new URLSearchParams(loc.search);
  return safeRedirectTarget(params.get("redirect_url"), fallback);
}

export function currentPathForRedirect(): string {
  const loc = readLocation();
  if (!loc) return "/";
  // Capture path + query + hash so deep links survive the sign-in trip.
  return `${loc.pathname}${loc.search}${loc.hash}`;
}

export function signInUrlWithReturn(target: string): string {
  const safe = safeRedirectTarget(target, "/");
  if (safe === "/") return "/sign-in";
  return `/sign-in?redirect_url=${encodeURIComponent(safe)}`;
}
